import { currentLang, t } from './index';
import type { AgeTier, FigureGroup, FigureVariant, StarterType } from '../types';

const STARTER_TYPE_KEY = {
  single: 'starterType.single',
  pair: 'starterType.pair',
  team4: 'starterType.team4',
  team6: 'starterType.team6',
} as const satisfies Record<StarterType, 'starterType.single' | 'starterType.pair' | 'starterType.team4' | 'starterType.team6'>;

/**
 * UI display label for a starter type — translatable, and used both in the
 * UI (`ui/starter-view.ts`, `ui/programm-view.ts`) and in `xml/validate.ts`'s
 * messages. Unrelated to `STARTER_TYPES[type].prefix`, which is the German
 * string the XML always uses regardless of UI language.
 */
export function starterTypeLabel(type: StarterType): string {
  return t(STARTER_TYPE_KEY[type]);
}

const AGE_TIER_KEY = {
  eliteJunior: 'ageTier.eliteJunior',
  youth: 'ageTier.youth',
} as const satisfies Record<AgeTier, 'ageTier.eliteJunior' | 'ageTier.youth'>;

/** UI display label for a `Programme.ageTier` — the UCI 8.2.002 (Elite/
 *  Junior) vs. 8.2.003 (Youth) figure-count tier. */
export function ageTierLabel(tier: AgeTier): string {
  return t(AGE_TIER_KEY[tier]);
}

export interface FigureVariantNames {
  /** The name in the current UI language — falls back to English when
   *  that's fr/es/cs and the rulebook has no translation for this specific
   *  variant (not every one does, see CLAUDE.md's i18n coverage notes). */
  readonly primary: string;
  /** A second, reference name in a different language: German, unless the
   *  primary already is German, in which case English. */
  readonly secondary: string;
}

/**
 * Display names for a figure variant in the programme editor and figure
 * picker (`src/ui/views/ProgrammeView.tsx`, `FigurePicker.tsx`) — driven by
 * the current UI language, unlike what actually gets exported (always
 * `FigureVariant.i18n.de`/`.en`, see `Exercise` in types.ts), which ignores
 * it. See "Figure names in the picker are a browsing aid" in CLAUDE.md.
 */
export function figureVariantNames(v: FigureVariant): FigureVariantNames {
  const lang = currentLang();
  return {
    primary: v.i18n[lang] ?? v.i18n.en ?? v.i18n.de ?? '',
    secondary: (lang === 'de' ? v.i18n.en : v.i18n.de) ?? '',
  };
}

/**
 * Translated title for a `FigureGroup` (the figure picker's section headers
 * and group filter) — read straight off the catalogue now that
 * `build-figures.py` sources every language's Chapter III section title
 * natively from that language's own rulebook mirror, the same way
 * figure/variant names are. Falls back to the English title for a language
 * the rulebook has no translation for, rather than showing nothing.
 */
export function figureGroupTitle(group: FigureGroup): string {
  const lang = currentLang();
  if (lang === 'de') return group.titleDe;
  if (lang === 'en') return group.title;
  return group.titleI18n?.[lang] ?? group.title;
}
